/**
 * Statically evaluates parseman combinator call expressions from an oxc AST
 * into actual Combinator<unknown> objects by calling the real library functions.
 *
 * Returns null for anything unresolvable (external variables, template literals,
 * computed keys, etc.) — callers leave those as-is.
 */
import type {
  Expression, Node,
  ArrowFunctionExpression, Function as OxcFunction,
  ReturnStatement,
  VariableDeclaration, VariableDeclarator,
  StaticMemberExpression,
  ObjectExpression, ObjectProperty,
} from '@oxc-project/types'
import type { Combinator } from '../types.ts'
import { ref } from '../combinators/ref.ts'
import * as parseman from '../index.ts'

// ---------------------------------------------------------------------------
// Scope types
//
// Each scope entry is either a raw Combinator, or an enriched entry that
// carries the mapFnSources this combinator will contribute when the codegen
// traverses its subtree.  The enriched form is needed so that anyValue can
// "replay" those sources when the combinator is referenced by another
// expression — keeping mapFnSources aligned with what ctx.mapFns builds.
// ---------------------------------------------------------------------------
export type ScopeEntry = {
  combi: Combinator<unknown>
  mfSrcs: string[]
}
export type Scope = Map<string, ScopeEntry>

// Internal XScope also holds non-Combinator values (g proxy objects etc.)
type XScopeVal = ScopeEntry | unknown
type XScope = Map<string, XScopeVal>

type WordFactoryEntry = { tag: 'wordFactory'; boundary: string }

function isWordFactory(v: unknown): v is WordFactoryEntry {
  return !!v && typeof v === 'object' && (v as WordFactoryEntry).tag === 'wordFactory'
}

const SUPPORTED: Record<string, (...args: unknown[]) => Combinator<unknown>> = {
  literal:   (...a) => parseman.literal(a[0] as string, a[1] as parseman.LiteralOptions | undefined),
  regex:     (...a) => parseman.regex(a[0] as RegExp, a[1] as string | undefined),
  keywords:  (...a) => parseman.keywords(a[0] as readonly string[], a[1] as parseman.KeywordsOptions | undefined),
  word:      (...a) => parseman.word(a[0] as string, a[1] as string | undefined),
  sequence:  (...a) => (parseman.sequence as (...p: Combinator<unknown>[]) => Combinator<unknown[]>)(...(a as Combinator<unknown>[])),
  choice:    (...a) => (parseman.choice as (...p: Combinator<unknown>[]) => Combinator<unknown>)(...(a as Combinator<unknown>[])),
  many:      (...a) => parseman.many(a[0] as Combinator<unknown>),
  oneOrMore: (...a) => parseman.oneOrMore(a[0] as Combinator<unknown>),
  optional:  (...a) => parseman.optional(a[0] as Combinator<unknown>),
  sepBy:     (...a) => parseman.sepBy(a[0] as Combinator<unknown>, a[1] as Combinator<unknown>),
  trivia:    (...a) => parseman.trivia(a[0] as Combinator<unknown>),
  label:     (...a) => parseman.label(a[0] as string, a[1] as Combinator<unknown>),
  noTrivia:  (...a) => parseman.noTrivia(a[0] as Combinator<unknown>),
  expect:    (...a) => parseman.expect(a[0] as Combinator<unknown>, a[1] as string | undefined),
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function isScopeEntry(v: unknown): v is ScopeEntry {
  return !!v && typeof v === 'object' && 'combi' in v && 'mfSrcs' in v
}

function isCombinator(v: unknown): v is Combinator<unknown> {
  return !!v && typeof v === 'object' && '_def' in v
}

/**
 * Resolve an identifier from scope.
 * If the entry carries mfSrcs, replay them into `mfs` so that the
 * overall accumulator stays aligned with what codegen will push.
 */
function scopeGet(scope: XScope, name: string, mfs?: string[]): Combinator<unknown> | null {
  const entry = scope.get(name)
  if (!entry) return null
  if (isScopeEntry(entry)) {
    if (mfs && entry.mfSrcs.length > 0) mfs.push(...entry.mfSrcs)
    return entry.combi
  }
  if (isCombinator(entry)) return entry
  return null
}

// ---------------------------------------------------------------------------
// Core evaluators
// ---------------------------------------------------------------------------

/** Read a static `{ collapse: true }` opts literal → whether collapse is enabled. */
function staticCollapseOption(expr: Expression): boolean {
  if (expr.type !== 'ObjectExpression') return false
  for (const prop of (expr as ObjectExpression).properties) {
    const p = prop as unknown as ObjectProperty
    if (p.computed) continue
    const key = p.key as unknown as { type: string; name?: string; value?: unknown }
    const name = key.type === 'Identifier' ? key.name
      : key.type === 'Literal' ? String(key.value)
      : undefined
    if (name === 'collapse') {
      const val = p.value as unknown as { type: string; value?: unknown }
      return (val.type === 'Literal' || val.type === 'BooleanLiteral') && val.value === true
    }
  }
  return false
}

/**
 * Evaluate a call expression to a Combinator.
 * `mfs` accumulates mapFn source texts in depth-first order — must match
 * what codegen pushes to ctx.mapFns when it traverses the same tree.
 */
function exprToCombi(node: Expression, scope: XScope, code?: string, mfs?: string[]): Combinator<unknown> | null {
  if (node.type === 'Identifier') return scopeGet(scope, node.name, mfs)

  if (node.type !== 'CallExpression') return null

  const callee = node.callee

  // makeWord(boundary)(str)
  if (callee.type === 'CallExpression'
    && callee.callee.type === 'Identifier'
    && callee.callee.name === 'makeWord') {
    const boundaryArg = callee.arguments[0]
    const strArg = node.arguments[0]
    if (!strArg || strArg.type === 'SpreadElement') return null
    const boundary = boundaryArg && boundaryArg.type !== 'SpreadElement'
      ? anyValue(boundaryArg as Expression, scope, code, mfs)
      : '_0-9A-Za-z'
    const str = anyValue(strArg as Expression, scope, code, mfs)
    if (typeof boundary !== 'string' || typeof str !== 'string') return null
    try { return parseman.word(str, boundary) } catch { return null }
  }

  if (callee.type === 'Identifier') {
    const factory = scope.get(callee.name)
    if (isWordFactory(factory)) {
      const [strArg] = node.arguments
      if (!strArg || strArg.type === 'SpreadElement') return null
      const str = anyValue(strArg as Expression, scope, code, mfs)
      if (typeof str !== 'string') return null
      try { return parseman.word(str, factory.boundary) } catch { return null }
    }
  }

  if (callee.type !== 'Identifier') return null

  // transform(inner, fn) — capture fn source text before pushing to mfs
  if (callee.name === 'transform' && code !== undefined && mfs !== undefined) {
    const [parserArg, fnArg] = node.arguments
    if (!parserArg || !fnArg || parserArg.type === 'SpreadElement' || fnArg.type === 'SpreadElement') return null
    const inner = anyValue(parserArg as Expression, scope, code, mfs)
    if (!isCombinator(inner)) return null
    const fnSrc = code.slice((fnArg as Expression).start, (fnArg as Expression).end)
    mfs.push(fnSrc)
    try {
      const combi = parseman.transform(inner, (v: unknown) => v)
      // Carry the callback source on the def so codegen can pull it in traversal
      // order (order-independent across rules that share sub-combinators).
      if (combi._def.tag === 'transform') combi._def.fnSrc = fnSrc
      return combi
    } catch { return null }
  }

  // node(type, parser, build, opts?) — CST node rule. Capture the build callback
  // source (like transform) so codegen inlines it; the inner parser carries the
  // capture. An optional 4th `{ collapse: true }` opts literal is read statically.
  if (callee.name === 'node' && code !== undefined) {
    const [typeArg, parserArg, buildArg, optsArg] = node.arguments
    if (!typeArg || !parserArg || !buildArg
      || typeArg.type === 'SpreadElement' || parserArg.type === 'SpreadElement' || buildArg.type === 'SpreadElement') return null
    const typeVal = anyValue(typeArg as Expression, scope, code)
    if (typeof typeVal !== 'string') return null
    const inner = anyValue(parserArg as Expression, scope, code, mfs)
    if (!isCombinator(inner)) return null
    const buildSrc = code.slice((buildArg as Expression).start, (buildArg as Expression).end)
    const collapse = optsArg !== undefined && optsArg.type !== 'SpreadElement'
      ? staticCollapseOption(optsArg as Expression)
      : false
    try {
      const combi = parseman.node(typeVal, inner, () => null, collapse ? { collapse: true } : undefined)
      if (combi._def.tag === 'node') combi._def.buildSrc = buildSrc
      return combi
    } catch { return null }
  }

  // rules(factory) — handled separately by evaluateParserFactory; signal null here
  if (callee.name === 'rules') return null

  // ref() — forward-declared recursion slot. Standalone refs (declared, then
  // resolved later via `x.define(...)`) are the interpreter/compile() recursion
  // mechanism; the macro must support them too for parity. We return a REAL ref
  // placeholder here; index.ts pre-resolves all `x.define(...)` statements into
  // scope before compilation so codegen's emitLazy sees a defined thunk.
  if (callee.name === 'ref') {
    if (node.arguments.length !== 0) return null
    return ref<unknown>() as Combinator<unknown>
  }

  // parser(opts, root) — bakes trivia/trackLines into a `grammar` combinator so
  // the compiled output skips whitespace between sequence terms identically to
  // the interpreter. opts.trivia is itself a combinator; evaluate it with a
  // throwaway mfs accumulator since the trivia parser is emitted out-of-band
  // (ensureTriviaFn) and its sources are pulled via def.fnSrc, not positionally.
  if (callee.name === 'parser') {
    const [optsArg, rootArg] = node.arguments
    if (!optsArg || !rootArg || optsArg.type === 'SpreadElement' || rootArg.type === 'SpreadElement') return null
    const opts = anyValue(optsArg as Expression, scope, code, [])
    if (!opts || typeof opts !== 'object') return null
    const root = anyValue(rootArg as Expression, scope, code, mfs)
    if (!isCombinator(root)) return null
    try {
      return parseman.parser(opts as parseman.ParserOptions, root)
    } catch { return null }
  }

  // sepBy(item, sep) — emitSepBy traverses: item (first probe), sep, item (loop body)
  // We must push item's mfSrcs twice to stay aligned with ctx.mapFns.
  if (callee.name === 'sepBy') {
    const [itemArg, sepArg] = node.arguments
    if (!itemArg || !sepArg || itemArg.type === 'SpreadElement' || sepArg.type === 'SpreadElement') return null
    const itemMfs: string[] = []
    const itemCombi = anyValue(itemArg as Expression, scope, code, itemMfs)
    if (!isCombinator(itemCombi)) return null
    const sepMfs: string[] = []
    const sepCombi = anyValue(sepArg as Expression, scope, code, sepMfs)
    if (!isCombinator(sepCombi)) return null
    if (mfs) mfs.push(...itemMfs, ...sepMfs, ...itemMfs)
    try { return parseman.sepBy(itemCombi, sepCombi) } catch { return null }
  }

  // oneOrMore(item) — emitMany(min=1) traverses: item (mandatory first), item (loop body)
  if (callee.name === 'oneOrMore') {
    const [itemArg] = node.arguments
    if (!itemArg || itemArg.type === 'SpreadElement') return null
    const itemMfs: string[] = []
    const itemCombi = anyValue(itemArg as Expression, scope, code, itemMfs)
    if (!isCombinator(itemCombi)) return null
    if (mfs) mfs.push(...itemMfs, ...itemMfs)
    try { return parseman.oneOrMore(itemCombi) } catch { return null }
  }

  // not(parser) — negative lookahead (consumes nothing).
  if (callee.name === 'not') {
    const [innerArg] = node.arguments
    if (!innerArg || innerArg.type === 'SpreadElement') return null
    const inner = anyValue(innerArg as Expression, scope, code, mfs)
    if (!isCombinator(inner)) return null
    try { return parseman.not(inner) } catch { return null }
  }

  // balanced(open, close, opts?) — like scanTo, opts (notably opts.skip, an array
  // of combinators) MUST be honored. The interpreter and compile() build the full
  // combinator structure from opts; the macro must evaluate and pass opts too —
  // dropping it silently produces wrong (parity-breaking) behavior.
  if (callee.name === 'balanced') {
    const [openArg, closeArg, optsArg] = node.arguments
    if (!openArg || !closeArg || openArg.type === 'SpreadElement' || closeArg.type === 'SpreadElement') return null
    const open = anyValue(openArg as Expression, scope, code, [])
    const close = anyValue(closeArg as Expression, scope, code, [])
    if (typeof open !== 'string' || typeof close !== 'string') return null
    const opts = optsArg && optsArg.type !== 'SpreadElement'
      ? anyValue(optsArg as Expression, scope, code, [])
      : undefined
    try { return parseman.balanced(open, close, opts as parseman.ScanToOptions | undefined) } catch { return null }
  }

  // scanTo(sentinel, opts?) — consume up to (and including) a sentinel, optionally
  // skipping balanced pairs. opts.skip is an array of combinators.
  if (callee.name === 'scanTo') {
    const [sentinelArg, optsArg] = node.arguments
    if (!sentinelArg || sentinelArg.type === 'SpreadElement') return null
    const sentinel = anyValue(sentinelArg as Expression, scope, code, [])
    if (!isCombinator(sentinel)) return null
    const opts = optsArg && optsArg.type !== 'SpreadElement'
      ? anyValue(optsArg as Expression, scope, code, [])
      : undefined
    try { return parseman.scanTo(sentinel, opts as parseman.ScanToOptions | undefined) } catch { return null }
  }

  const factory = SUPPORTED[callee.name]
  if (!factory) return null

  const args = node.arguments.map(arg => {
    if (arg.type === 'SpreadElement') return null
    return anyValue(arg as Expression, scope, code, mfs)
  })
  if (args.some(a => a === null)) return null

  try {
    return factory(...(args as unknown[]))
  } catch { return null }
}

/** Evaluate any expression to its JS value (not necessarily a Combinator). */
function anyValue(node: Expression, scope: XScope, code?: string, mfs?: string[]): unknown {
  if (node.type === 'Literal') {
    if ('regex' in node && node.regex !== null && node.regex !== undefined) {
      return new RegExp(node.regex.pattern, node.regex.flags)
    }
    return node.value
  }

  if (node.type === 'ArrayExpression') {
    const arr = node as unknown as { elements: Array<Expression | null> }
    const out: unknown[] = []
    for (const el of arr.elements) {
      if (el === null) { out.push(null); continue }
      if ((el as { type: string }).type === 'SpreadElement') return null
      out.push(anyValue(el as Expression, scope, code, mfs))
    }
    return out
  }

  if (node.type === 'ObjectExpression') {
    const obj: Record<string, unknown> = {}
    for (const prop of node.properties) {
      if (prop.type !== 'Property') return null
      if ((prop as unknown as ObjectProperty).computed) return null
      const key = (prop as unknown as ObjectProperty).key.type === 'Identifier'
        ? ((prop as unknown as ObjectProperty).key as { name: string }).name
        : (prop as unknown as ObjectProperty).key.type === 'Literal'
        ? String(((prop as unknown as ObjectProperty).key as { value: unknown }).value)
        : null
      if (key === null) return null
      obj[key] = anyValue((prop as unknown as ObjectProperty).value as Expression, scope, code, mfs)
    }
    return obj
  }

  if (node.type === 'Identifier') {
    if (node.name === 'undefined') return undefined
    const entry = scope.get(node.name) ?? null
    if (isScopeEntry(entry)) {
      if (mfs && entry.mfSrcs.length > 0) mfs.push(...entry.mfSrcs)
      return entry.combi
    }
    return entry
  }

  // MemberExpression — handles g.ruleName references inside parser() factories
  if (node.type === 'MemberExpression') {
    const mem = node as unknown as StaticMemberExpression
    const obj = anyValue(mem.object as Expression, scope, code, mfs)
    if (!obj || typeof obj !== 'object') return null
    if ((node as unknown as { computed: boolean }).computed) {
      const key = anyValue((node as unknown as { property: Expression }).property, scope, code, mfs)
      if (typeof key !== 'string' && typeof key !== 'number') return null
      return (obj as Record<string | number, unknown>)[key] ?? null
    }
    const propName = (mem.property as { name?: string }).name
    if (!propName) return null
    return (obj as Record<string, unknown>)[propName] ?? null
  }

  if (node.type === 'CallExpression') {
    const callee = node.callee
    if (callee.type === 'Identifier' && callee.name === 'makeWord') {
      const boundaryArg = node.arguments[0]
      const boundary = boundaryArg && boundaryArg.type !== 'SpreadElement'
        ? anyValue(boundaryArg as Expression, scope, code, mfs)
        : '_0-9A-Za-z'
      if (typeof boundary !== 'string') return null
      return { tag: 'wordFactory', boundary } satisfies WordFactoryEntry
    }
    return exprToCombi(node, scope, code, mfs)
  }

  return null
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Evaluate makeWord(boundary?) to a factory entry (not a combinator). */
export function evaluateWordFactory(
  node: Expression,
  scope: Scope,
  code?: string,
): WordFactoryEntry | null {
  if (node.type !== 'CallExpression') return null
  const callee = node.callee
  if (callee.type !== 'Identifier' || callee.name !== 'makeWord') return null
  const boundaryArg = node.arguments[0]
  const boundary = boundaryArg && boundaryArg.type !== 'SpreadElement'
    ? anyValue(boundaryArg as Expression, scope as XScope, code)
    : '_0-9A-Za-z'
  if (typeof boundary !== 'string') return null
  return { tag: 'wordFactory', boundary }
}

/** Evaluate a single combinator expression. Returns null if unresolvable. */
export function evaluateExpr(
  node: Expression,
  scope: Scope,
  code?: string,
  mapFnSources?: string[],
): Combinator<unknown> | null {
  return exprToCombi(node, scope as XScope, code, mapFnSources)
}

/**
 * Evaluate a `const X = [combinator, …]` array literal into an array of
 * Combinators. Lets a shared option array (e.g. a `skip` set reused across
 * `scanTo`/`balanced` calls) be referenced by name — `{ skip: X }` — instead of
 * inlining the array at every call site. Returns null when `node` isn't an array
 * literal of statically-resolvable combinators.
 */
export function evaluateCombinatorArray(
  node: Expression,
  scope: Scope,
  code?: string,
): Combinator<unknown>[] | null {
  if (node.type !== 'ArrayExpression') return null
  const val = anyValue(node, scope as XScope, code, [])
  if (!Array.isArray(val) || val.length === 0) return null
  if (!val.every(isCombinator)) return null
  return val as Combinator<unknown>[]
}

// ---------------------------------------------------------------------------
// Grammar composition — inlining `...fragment(g)` spreads in a rules() map.
//
// A fragment is a factory `(g, deps?) => ({ rule: combinator, … })`. Spreading
// its result into a rules() return object composes it in. To keep the compiled
// (macro) fast path, the macro resolves the fragment's factory AST and inlines
// its rules into the consumer's rule map — as if they'd been written inline —
// evaluating them against the CONSUMER's `g` proxy so cross-fragment `g.*` refs
// resolve. Spread order = override order (a later definition of a key wins).
// ---------------------------------------------------------------------------

/** A rules() fragment factory AST: an arrow / function returning an object literal. */
export type FactoryAst = ArrowFunctionExpression | OxcFunction

/**
 * A resolved fragment factory + the source text its AST offsets index into. For a
 * same-file factory that's the consumer's own `code`; for an imported one it's the
 * exporting module's source (read by the plugin). The source MUST travel with the
 * AST so callback capture (`node`/`transform` slice `code[start..end]`) reads the
 * right bytes — a fragment's node offsets are meaningless against the consumer file.
 */
export type ResolvedFactory = { fn: FactoryAst; code: string }

/**
 * Resolve a fragment factory by name — a local same-file `const frag = …` OR an
 * imported one whose source the plugin reads (tier 2). Returns null when the name
 * isn't a resolvable fragment (→ the spread bails to the interpreter fallback).
 */
export type FactoryResolver = (name: string) => ResolvedFactory | null

/**
 * If `expr` is a fragment factory — an arrow/function whose body returns an object
 * literal, e.g. `(g, deps) => ({ rule: combinator, … })` — return it (for the macro
 * to register and inline into `...frag(g)` spreads). Otherwise null.
 */
export function asFragmentFactory(expr: Expression): FactoryAst | null {
  if (expr.type !== 'ArrowFunctionExpression' && expr.type !== 'FunctionExpression' && expr.type !== 'FunctionDeclaration') return null
  const fn = expr as unknown as FactoryAst
  return factoryBodyAndReturn(fn) ? fn : null
}

const unwrapParen = (e: unknown): unknown =>
  e && (e as { type?: string }).type === 'ParenthesizedExpression'
    ? (e as { expression: unknown }).expression
    : e

/**
 * A fragment factory's body statements (local `const`s) + its returned object
 * literal. Supports concise `(g) => ({ … })` and block `(g) => { const … ; return { … } }`.
 * Returns null for anything else (non-object body, no return, etc.).
 */
function factoryBodyAndReturn(fn: FactoryAst): { statements: VariableDeclaration[]; retObj: ObjectExpression } | null {
  const body = (fn as unknown as { body?: unknown }).body
  if (!body) return null
  const b = unwrapParen(body) as { type: string; body?: unknown[] }
  if (b.type === 'ObjectExpression') return { statements: [], retObj: b as unknown as ObjectExpression }
  if (b.type === 'BlockStatement') {
    const statements: VariableDeclaration[] = []
    for (const stmt of b.body as Array<{ type: string; argument?: unknown }>) {
      if (stmt.type === 'ReturnStatement') {
        const a = unwrapParen(stmt.argument) as { type: string } | null
        if (!a || a.type !== 'ObjectExpression') return null
        return { statements, retObj: a as unknown as ObjectExpression }
      }
      if (stmt.type === 'VariableDeclaration') statements.push(stmt as unknown as VariableDeclaration)
      else return null // unsupported statement in a fragment body
    }
  }
  return null
}

/** Resolve a `...frag(args)` spread element to its registered factory + call args + source. */
function spreadFactoryCall(
  spread: { argument?: unknown }, resolve: FactoryResolver,
): { fn: FactoryAst; args: Expression[]; code: string } | null {
  const call = spread.argument as { type?: string; callee?: { type?: string; name?: string }; arguments?: unknown[] } | undefined
  if (!call || call.type !== 'CallExpression') return null
  if (!call.callee || call.callee.type !== 'Identifier' || !call.callee.name) return null
  const resolved = resolve(call.callee.name)
  return resolved ? { fn: resolved.fn, args: (call.arguments ?? []) as Expression[], code: resolved.code } : null
}

/** Bind a fragment's identifier params to the resolved call args (e.g. `g` → the proxy). */
function factoryCallScope(fn: FactoryAst, args: Expression[], outer: XScope): XScope {
  const s: XScope = new Map(outer)
  const params = ((fn as unknown as { params?: Array<{ type: string; name?: string }> }).params) ?? []
  for (let i = 0; i < params.length; i++) {
    const pn = params[i]
    if (!pn || pn.type !== 'Identifier' || !pn.name) continue
    const arg = args[i] as { type?: string; name?: string } | undefined
    // Only identifier args that name something in scope (the `g` proxy) are bound.
    // Non-identifier args (e.g. a `{ build }` deps literal) are left unbound — their
    // uses inside build callbacks are captured as source and resolve in the consumer.
    if (arg && arg.type === 'Identifier' && arg.name && outer.has(arg.name)) {
      s.set(pn.name, outer.get(arg.name))
    }
  }
  return s
}

/** Extract just the property key of a rules() return `Property`, or null. */
function propKey(prop: { type: string; computed?: boolean; key?: { type: string; name?: string; value?: unknown } }): string | null {
  if (prop.type !== 'Property' || prop.computed || !prop.key) return null
  if (prop.key.type === 'Identifier') return prop.key.name ?? null
  if (prop.key.type === 'Literal') return String(prop.key.value)
  return null
}

/** Collect every rule key from a rules() return object, flattening spreads. */
function collectRuleKeys(retObj: ObjectExpression, resolve: FactoryResolver, seen: Set<FactoryAst>): string[] | null {
  const out: string[] = []
  for (const prop of (retObj as unknown as { properties: Array<{ type: string }> }).properties) {
    if (prop.type === 'SpreadElement') {
      const sf = spreadFactoryCall(prop as { argument?: unknown }, resolve)
      if (!sf || seen.has(sf.fn)) return null
      const br = factoryBodyAndReturn(sf.fn)
      if (!br) return null
      const nested = collectRuleKeys(br.retObj, resolve, new Set(seen).add(sf.fn))
      if (!nested) return null
      out.push(...nested)
      continue
    }
    const key = propKey(prop as never)
    if (!key) return null
    out.push(key)
  }
  return out
}

type RuleEntry = { key: string; value: Expression; scope: XScope; code: string }

/**
 * Flatten a rules() return object into ordered (key, valueExpr, evalScope), inlining
 * `...frag(g)` spreads. A fragment's body `const`s are evaluated into a fragment-local
 * scope (params bound to the call args), so its rules resolve their locals + the shared
 * `g` proxy. Returns null if any spread is unresolvable (→ interpreter fallback).
 */
function flattenRuleEntries(
  retObj: ObjectExpression, scope: XScope, code: string,
  resolve: FactoryResolver, seen: Set<FactoryAst>,
): RuleEntry[] | null {
  const out: RuleEntry[] = []
  for (const prop of (retObj as unknown as { properties: Array<{ type: string; value?: unknown }> }).properties) {
    if (prop.type === 'SpreadElement') {
      const sf = spreadFactoryCall(prop as { argument?: unknown }, resolve)
      if (!sf || seen.has(sf.fn)) return null
      const br = factoryBodyAndReturn(sf.fn)
      if (!br) return null
      const fragScope = factoryCallScope(sf.fn, sf.args, scope)
      // Evaluate the fragment against ITS OWN source (sf.code) — its AST offsets
      // index into that, not the consumer's `code`. Same-file fragments pass the
      // same string, so this is a no-op there.
      if (!evalBodyStatements(br.statements, fragScope, sf.code)) return null
      const nested = flattenRuleEntries(br.retObj, fragScope, sf.code, resolve, new Set(seen).add(sf.fn))
      if (!nested) return null
      out.push(...nested)
      continue
    }
    const key = propKey(prop as never)
    if (!key) return null
    out.push({ key, value: (prop as { value: Expression }).value, scope, code })
  }
  return out
}

/** Evaluate a factory body's `const` declarations into `scope`. Returns false on failure. */
function evalBodyStatements(statements: VariableDeclaration[], scope: XScope, code: string): boolean {
  const bodyMfs: string[] = []
  for (const stmt of statements) {
    for (const d of stmt.declarations) {
      const decl = d as unknown as VariableDeclarator
      if (!decl.init) return false
      const id = decl.id as unknown as { type: string; name?: string }
      if (id.type !== 'Identifier' && id.type !== 'BindingIdentifier') return false
      const name = id.name!
      const before = bodyMfs.length
      const val = anyValue(decl.init as unknown as Expression, scope, code, bodyMfs)
      if (val === null) return false
      const thisDeclMfSrcs = bodyMfs.slice(before)
      if (isCombinator(val)) scope.set(name, { combi: val, mfSrcs: thisDeclMfSrcs } satisfies ScopeEntry)
      else scope.set(name, val)
    }
  }
  return true
}

/**
 * Evaluate a `parser(g => { ... return { ruleName: combinator, ... } })` call.
 * Returns a map of rule names → defined Combinators, or null if the factory
 * can't be statically evaluated.
 *
 * mapFnSources is populated with the sources for mapFns that codegen will push
 * when compiling each returned rule — each rule's entry in the map will produce
 * a sub-slice of mapFnSources aligned to its specific ctx.mapFns.
 *
 * Important: this function uses a SEPARATE accumulator for body statement
 * evaluation so that only the return-expression phase adds entries to the
 * caller-provided mapFnSources (which is what compile() will receive).
 * The body-phase entries are stored as `mfSrcs` on localScope entries and
 * replayed when those entries are referenced during return evaluation.
 */
export function evaluateParserFactory(
  factoryNode: Expression,
  scope: Scope,
  code: string,
  mapFnSources: string[],  // receives ONLY the return-expression mfSrcs
  resolveFactory: FactoryResolver = () => null,  // resolves `...frag(g)` spread factories (same-file + imported)
): Map<string, Combinator<unknown>> | null {
  if (factoryNode.type !== 'ArrowFunctionExpression' && factoryNode.type !== 'FunctionDeclaration' && factoryNode.type !== 'FunctionExpression') return null

  const factory = factoryNode as unknown as ArrowFunctionExpression | OxcFunction
  const params = factory.params
  if (params.length !== 1) return null
  const param = params[0] as unknown as { type: string; name?: string }
  // FormalParameter is { decorators? } & BindingPattern — BindingIdentifier has type "Identifier"
  const proxyName = param.type === 'Identifier' ? param.name ?? null : null
  if (!proxyName) return null

  const body = factory.body
  if (!body) return null
  const statements: VariableDeclaration[] = []
  let returnExpr: Expression | null = null

  if ((body as unknown as { type: string }).type === 'BlockStatement') {
    const stmts = (body as unknown as { body: unknown[] }).body
    for (const stmt of stmts) {
      const s = stmt as { type: string }
      if (s.type === 'ReturnStatement') {
        returnExpr = ((s as unknown as ReturnStatement).argument ?? null) as Expression | null
        break
      }
      if (s.type === 'VariableDeclaration') {
        statements.push(s as unknown as VariableDeclaration)
      } else {
        return null // unsupported statement type
      }
    }
  } else {
    // Concise arrow body: g => ({ ... })
    returnExpr = body as unknown as Expression
  }

  if (!returnExpr) return null

  // Unwrap parenthesized expression if needed
  const retObj = returnExpr.type === 'ParenthesizedExpression'
    ? (returnExpr as unknown as { expression: Expression }).expression
    : returnExpr
  if (retObj.type !== 'ObjectExpression') return null

  // Pre-scan return object to get rule names — flattening any `...frag(g)` spreads
  // (composition). An unresolvable spread → null → the caller falls back to the
  // interpreter. One ref per UNIQUE key (first occurrence); a later spread/property
  // of the same name overrides the value in Phase 2, not the ref.
  const keys = collectRuleKeys(retObj as unknown as ObjectExpression, resolveFactory, new Set())
  if (!keys) return null
  const ruleRefs = new Map<string, Combinator<unknown> & { define(p: Combinator<unknown>): void }>()
  for (const key of keys) {
    if (!ruleRefs.has(key)) ruleRefs.set(key, ref<unknown>() as Combinator<unknown> & { define(p: Combinator<unknown>): void })
  }

  // Build local extended scope: outer scope (typed as XScope) + g proxy object.
  // Note: outer ScopeEntry values carry their mfSrcs and will be replayed by
  // scopeGet() when body statements or return expressions reference them.
  const localScope: XScope = new Map(scope as XScope)
  localScope.set(proxyName, Object.fromEntries(ruleRefs))

  // ── Phase 1: evaluate the factory's own body statements ───────────────────
  if (!evalBodyStatements(statements, localScope, code)) return null

  // ── Phase 2: flatten (inlining spreads) → dedup last-wins → eval + define ──
  // flattenRuleEntries evaluates each fragment's body `const`s into a fragment-local
  // scope (its params bound to the call args — `g` → this proxy) and returns the
  // ordered (key, valueExpr, scope). Spread order = override order: the LAST value
  // for a key wins (refs throw on double-define, so each key is defined exactly once).
  const entries = flattenRuleEntries(retObj as unknown as ObjectExpression, localScope, code, resolveFactory, new Set())
  if (!entries) return null
  const finalByKey = new Map<string, RuleEntry>()
  for (const e of entries) finalByKey.set(e.key, e) // set() keeps first insert position, updates value → last wins
  for (const [key, e] of finalByKey) {
    const val = anyValue(e.value, e.scope, e.code, mapFnSources)
    if (!isCombinator(val)) return null
    ruleRefs.get(key)!.define(val as Combinator<unknown>)
  }

  return ruleRefs as Map<string, Combinator<unknown>>
}

/** A combinator slot created by ref() — has a callable `define`. */
type DefinableRef = Combinator<unknown> & { define(p: Combinator<unknown>): void }

function isDefinableRef(v: unknown): v is DefinableRef {
  return isCombinator(v)
    && typeof (v as { define?: unknown }).define === 'function'
    && (v as { _def: { tag?: string } })._def.tag === 'lazy'
}

/**
 * If `init` is a bare `ref()` call, evaluate it to a real ref placeholder and
 * register it in scope under `name`. Returns the ref, or null if `init` isn't
 * a `ref()` call. Used by the macro pre-pass so standalone refs resolve before
 * compilation (parity with the interpreter / compile()).
 */
export function evaluateRefDeclaration(
  init: Expression,
  name: string,
  scope: Scope,
): DefinableRef | null {
  if (init.type !== 'CallExpression') return null
  const callee = (init as unknown as { callee: { type: string; name?: string } }).callee
  if (callee.type !== 'Identifier' || callee.name !== 'ref') return null
  if ((init as unknown as { arguments: unknown[] }).arguments.length !== 0) return null
  const slot = ref<unknown>() as DefinableRef
  ;(scope as XScope).set(name, { combi: slot, mfSrcs: [] } satisfies ScopeEntry)
  return slot
}

/**
 * Apply a `someRef.define(expr)` statement: resolve the target ref from scope,
 * evaluate the argument to a combinator, and call `.define()`. Returns true on
 * success. The macro removes the original statement from the output (it would
 * otherwise reference the stripped import); returning false signals "leave it".
 */
export function applyDefineStatement(
  callExpr: Expression,
  scope: Scope,
  code: string,
): boolean {
  if (callExpr.type !== 'CallExpression') return false
  const callee = (callExpr as unknown as { callee: { type: string } }).callee
  if (callee.type !== 'MemberExpression') return false
  const mem = callee as unknown as { object: { type: string; name?: string }; property: { type: string; name?: string }; computed: boolean }
  if (mem.computed) return false
  if (mem.property.type !== 'Identifier' || mem.property.name !== 'define') return false
  if (mem.object.type !== 'Identifier' || !mem.object.name) return false

  const target = (scope as XScope).get(mem.object.name)
  const refCombi = isScopeEntry(target) ? target.combi : (isCombinator(target) ? target : null)
  if (!refCombi || !isDefinableRef(refCombi)) return false

  const args = (callExpr as unknown as { arguments: Array<{ type: string }> }).arguments
  if (args.length !== 1 || args[0]!.type === 'SpreadElement') return false
  const inner = anyValue(args[0] as unknown as Expression, scope as XScope, code, [])
  if (!isCombinator(inner)) return false

  try { refCombi.define(inner) } catch { return false }
  return true
}

/** Check if an AST node references any name from the given scope or names set. */
export function referencesAny(node: Node, names: Set<string>, scope: Scope): boolean {
  if (node.type === 'Identifier') {
    return names.has(node.name) || scope.has(node.name)
  }
  for (const key of Object.keys(node) as (keyof typeof node)[]) {
    const child = node[key]
    if (!child || typeof child !== 'object') continue
    if (Array.isArray(child)) {
      if (child.some(c => c && typeof c === 'object' && 'type' in c && referencesAny(c as Node, names, scope))) return true
    } else if ('type' in child) {
      if (referencesAny(child as Node, names, scope)) return true
    }
  }
  return false
}
